import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastItem {
  id: number;
  type: ToastType;
  message: string;
}

interface ConfirmState {
  open: boolean;
  message: string;
  resolve: ((v: boolean) => void) | null;
}

interface ToastContextType {
  toast: (message: string, type?: ToastType) => void;
  confirm: (message: string) => Promise<boolean>;
}

const ToastContext = createContext<ToastContextType>({
  toast: () => {},
  confirm: async () => false,
});

export const useToast = () => useContext(ToastContext);

let _id = 0;

const COLORS: Record<ToastType, { bg: string; border: string; icon: string }> = {
  success: { bg: '#052e16', border: 'rgba(34,197,94,0.3)', icon: '✓' },
  error:   { bg: '#450a0a', border: 'rgba(239,68,68,0.3)',  icon: '✕' },
  warning: { bg: '#431407', border: 'rgba(249,115,22,0.3)', icon: '⚠' },
  info:    { bg: '#0c1a2e', border: 'rgba(59,130,246,0.3)', icon: 'ℹ' },
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [confirmState, setConfirmState] = useState<ConfirmState>({ open: false, message: '', resolve: null });

  const toast = useCallback((message: string, type: ToastType = 'info') => {
    const id = ++_id;
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4500);
  }, []);

  const confirm = useCallback((message: string): Promise<boolean> => {
    return new Promise(resolve => {
      setConfirmState({ open: true, message, resolve });
    });
  }, []);

  const handleConfirm = (value: boolean) => {
    if (confirmState.resolve) confirmState.resolve(value);
    setConfirmState({ open: false, message: '', resolve: null });
  };

  return (
    <ToastContext.Provider value={{ toast, confirm }}>
      {children}

      {/* Toast stack */}
      <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999, display: 'flex', flexDirection: 'column-reverse', gap: 8, pointerEvents: 'none' }}>
        {toasts.map(t => {
          const c = COLORS[t.type];
          return (
            <div key={t.id} style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              padding: '12px 16px', borderRadius: 10,
              background: c.bg, border: `1px solid ${c.border}`,
              color: '#f1f5f9', fontSize: 13, fontWeight: 500,
              maxWidth: 380, boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              animation: 'tSlide 0.22s ease',
              pointerEvents: 'auto',
            }}>
              <span style={{ fontSize: 14, marginTop: 0.5, flexShrink: 0 }}>{c.icon}</span>
              <span style={{ lineHeight: 1.5 }}>{t.message}</span>
            </div>
          );
        })}
      </div>

      {/* Confirm dialog */}
      {confirmState.open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998 }}>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '28px 28px 22px', maxWidth: 400, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 22, lineHeight: 1.5 }}>
              {confirmState.message}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => handleConfirm(false)} style={{ padding: '9px 20px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={() => handleConfirm(true)} style={{ padding: '9px 20px', background: 'linear-gradient(135deg, #dc2626, #b91c1c)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes tSlide { from { transform: translateX(20px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
    </ToastContext.Provider>
  );
};
